/**
 * The Sports you follow screen's rules, with no renderer and no fetch
 * (ISSUE-1400). It reuses onboarding's grouping, with every sport shown,
 * because a fan editing later is browsing rather than narrowing down.
 */
import {
  buildLeagueGroups,
  resolveInitialLeagueIds,
  type typeOnboardingLeagueGroup,
} from "../onboarding/onboardingFollowsModel";
import type { typeCatalogSportInput } from "../scores/leagueSelectorRowModel";

export const FOLLOWS_HEADING = "Sports you follow";
export const FOLLOWS_SUB =
  "Pick the leagues you want scores and news for. Change it any time.";
export const FOLLOWS_GROUP_LABEL_SUFFIX = "leagues";
export const FOLLOWS_SAVE_LABEL = "Save";
export const FOLLOWS_SAVING_LABEL = "Saving";
export const FOLLOWS_CANCEL_LABEL = "Cancel";
export const FOLLOWS_SAVED_NOTE = "Saved";
export const FOLLOWS_EMPTY_TITLE = "No leagues yet";
export const FOLLOWS_EMPTY_BODY =
  "We're adding leagues soon. Check back in a bit.";
export const FOLLOWS_LOAD_FAILED_TITLE = "We couldn't load your leagues";
export const FOLLOWS_LOAD_FAILED_BODY = "Check your connection and try again.";
export const FOLLOWS_DISCARD_TITLE = "Leave without saving?";
export const FOLLOWS_DISCARD_BODY = "Your new picks will not be kept.";
export const FOLLOWS_DISCARD_LABEL = "Leave";
export const FOLLOWS_ROUTE = "/account/follows";

export function buildFollowsGroups(
  sports: typeCatalogSportInput[],
): typeOnboardingLeagueGroup[] {
  return buildLeagueGroups({ sports, selectedSportIds: [] });
}

/**
 * The leagues to show as followed when the screen opens: the fan's saved
 * leagues that are still pickable, in the order they followed them.
 */
export function resolveSavedLeagueIds(input: {
  sports: typeCatalogSportInput[];
  followedLeagueIds: readonly string[];
}): string[] {
  return resolveInitialLeagueIds({
    sports: input.sports,
    selectedSportIds: [],
    followedLeagueIds: [...input.followedLeagueIds],
  });
}

/**
 * The league list to send on Save (ISSUE-1378). The screen only offers
 * leagues a fan can pick today, so a league they followed earlier that is now
 * hidden or coming soon is not on screen. A save made about other leagues must
 * not drop it: it is kept after the picks, in the order it was followed,
 * without duplicates.
 */
export function leagueIdsToSave(input: {
  sports: typeCatalogSportInput[];
  draft: readonly string[];
  followedLeagueIds: readonly string[];
}): string[] {
  const shownLeagueIds = new Set<string>();
  for (const group of buildFollowsGroups(input.sports)) {
    for (const league of group.leagues) {
      if (!league.isComingSoon) shownLeagueIds.add(league.leagueId);
    }
  }
  const out = [...input.draft];
  for (const leagueId of input.followedLeagueIds) {
    if (shownLeagueIds.has(leagueId)) continue;
    if (out.includes(leagueId)) continue;
    out.push(leagueId);
  }
  return out;
}

/** True when the picks differ from what was saved, in any order. */
export function isFollowsDirty(input: {
  saved: readonly string[];
  draft: readonly string[];
}): boolean {
  if (input.saved.length !== input.draft.length) return true;
  const saved = new Set(input.saved);
  return input.draft.some((leagueId) => !saved.has(leagueId));
}
